import { Component, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';
import { getGrowProjectBadgeDetails, GrowProject, GrowProjectStage, GrowProjectStatus, getGroupedInvestments } from '@inyova/grow';
import { AmplitudeService } from '@inyova/inyova-shared';
import { GrowAccount, InyovaGrowAccountStatus } from '@inyova/models';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-grow-project',
  templateUrl: './grow-project.component.html',
  styleUrls: ['./grow-project.component.scss'],
  standalone: false
})
export class GrowProjectComponent implements OnInit, OnChanges {
  @Input() project: GrowProject;
  @Input() growAccount: GrowAccount;
  @Input() percentage: number;
  @Input() isLoading = false;

  badge: { type: string; key: string };
  growProjectStatus = GrowProjectStatus;
  projectStageProgress: { type: string; percentage: number };
  language: string;
  investable: boolean;
  groupedInvestments: { status: GrowProjectStatus; amount: number }[];

  constructor(
    private translateService: TranslateService,
    private amplitudeService: AmplitudeService
  ) {
    this.language = this.translateService.currentLang;
  }

  ngOnInit() {
    this.getDetails();
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes && changes.project && !changes.project.firstChange) {
      this.getDetails();
    }
  }

  getDetails(): void {
    const { groupedInvestments, badge, projectStageProgress, investable } = this.getProjectDetails(this.project, this.growAccount);
    this.groupedInvestments = groupedInvestments;
    this.badge = badge;
    this.projectStageProgress = projectStageProgress;
    this.investable = investable;
  }

  getProjectDetails(
    project: GrowProject,
    growAccount: GrowAccount
  ): {
    groupedInvestments: { status: GrowProjectStatus; amount: number }[];
    badge: { type: string; key: string };
    projectStageProgress: { type: string; percentage: number };
    investable: boolean;
  } {
    const groupedInvestments = getGroupedInvestments(project.investments);
    const badge = getGrowProjectBadgeDetails(groupedInvestments[0].status);

    const projectStageProgress = getGrowProjectStageProgressDetail(project.stage);

    const investable =
      project.stage === GrowProjectStage.INVESTABLE &&
      growAccount.step_status === InyovaGrowAccountStatus.FUNDED &&
      project.account_specific.maximum_regulatory_additional_amount !== null &&
      project.account_specific.maximum_regulatory_additional_amount > 0 &&
      groupedInvestments[0].status !== GrowProjectStatus.INITIAL;

    return {
      groupedInvestments,
      badge,
      projectStageProgress,
      investable
    };
  }
}

const getGrowProjectStageProgressDetail = (stage: GrowProjectStage): { type: string; percentage: number } => {
  switch (stage) {
    case GrowProjectStage.INVESTABLE:
      return { type: 'info', percentage: 25 };
    case GrowProjectStage.CLOSED:
      return { type: 'warning', percentage: 50 };
    case GrowProjectStage.RUNNING:
      return { type: 'secondary', percentage: 75 };
    case GrowProjectStage.ENDED:
      return { type: 'success', percentage: 100 };
    default:
      return { type: 'info', percentage: 0 };
  }
};
